import { zodResolver } from '@hookform/resolvers/zod';
import {
  Checkbox,
  FormControl,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SaveButton from 'components/common/save-button';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import { ListingEntry } from 'graphql/listings/queries';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewTemplateType } from 'types/auto-templates';
import { z } from 'zod';
import validations from 'utils/validations';

import {
  getCodeSelector,
  emptyTemplate,
  perElemts,
} from '../utils/constants';

type FormTemplateProps = {
	handleOnSubmit: (data: NewTemplateType) => Promise<boolean>;
	template: NewTemplateType;
	codeTemplates?: string[];
	closeModal?: () => void;
	partyRoles: ListingEntry[],
	transactionTypes: ListingEntry[],
	types: ListingEntry[],
	workflows: ListingEntry[],
	isSubmitting: boolean;
	listingsReady: boolean;
	formId?: string;
	hideSubmit?: boolean;
	onValidityChange?: (isValid: boolean) => void;
};

const useStyles = makeStyles((theme: any) => ({
  input: {
    '&.MuiInputBase-formControl': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
      marginTop: 0,
      '& .MuiSelect-select': { padding: '27px 12px 10px' },
    },
  },
  saveAction: {
    '& .MuiButton-root': {
      minHeight: theme.spacing(5.5),
    },
  },
}));

const projectedPaymentsListOptions = [
  { code: '1', description: '1' },
  { code: '2', description: '2' },
  { code: '3', description: '3' },
  { code: '4', description: '4' },
  { code: 'All', description: 'All' }
];

export const FormTemplate: FC<FormTemplateProps> = ({
  handleOnSubmit,
  template,
  codeTemplates,
  closeModal,
  partyRoles,
  transactionTypes,
  types,
  workflows,
  isSubmitting,
  listingsReady,
  formId,
  hideSubmit = false,
  onValidityChange,
}) => {
  const { t } = useTranslation();
  const isEdit = !!template._id;
  const schema = z.object({
    data: z.object({
      property: z.any(),
    }).passthrough(),
    name: validations.requiredString(t('validations:required')),
    code: z.string()
      .min(1, t('documents:code-required'))
      .refine((value) => !/[A-Z]/.test(value || ''), t('validations:invalid-uppercase')),
    kindTem: isEdit
      ? z.string().optional()
      : validations.requiredString(t('validations:required')),
  }).passthrough().superRefine((formData, ctx) => {
    const propertyValue = formData.data?.property;
    const isPropertyValid = typeof propertyValue === 'string'
      ? propertyValue.trim().length > 0
      : Array.isArray(propertyValue) && propertyValue.length > 0;
    if (!isPropertyValid) {
      ctx.addIssue({
        path: ['data', 'property'],
        code: z.ZodIssueCode.custom,
        message: t('documents:property-required'),
      });
    }

    const fileName = `${formData.code}${getCodeSelector(t).find((codeSel) => codeSel.key === formData.kindTem)
      ?.extension}`;
    if (codeTemplates?.includes(fileName)) {
      ctx.addIssue({ path: ['code'], code: z.ZodIssueCode.custom, message: t('documents:code-repeated') });
    }
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<NewTemplateType>({
    mode: 'onChange',
    defaultValues: {
      kindTem: '',
      ...emptyTemplate,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const signersFiltered = listingsReady
      ? template.data.signer.filter((signer) => partyRoles.find((party) => party.code === signer))
      : template.data.signer;

    const partiesFiltered = listingsReady
      ? template.data.party.filter((partyFill) => partyRoles.find((party) => party.code === partyFill))
      : template.data.party;

    const deliversFiltered = listingsReady
      ? template.data.deliver.filter((deliver) => partyRoles.find((party) => party.code === deliver))
      : template.data.deliver;

    const templateFiltered = {
      ...template,
      data: {
        ...template.data,
        signer: signersFiltered,
        deliver: deliversFiltered,
        party: partiesFiltered,
      },
    };

    reset({
      kindTem: '',
      ...(template._id ? templateFiltered : template),
    });
  }, [template, reset, partyRoles, listingsReady]);

  useEffect(() => {
    if (onValidityChange) onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  const classes = useStyles();
  const controlsDisabled = isSubmitting || !listingsReady;

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(async (data) => {
			  const codeSelected = data.kindTem;
			  delete data.kindTem;
			  data.code = `${data.code}${getCodeSelector(t).find((code) => code.key === codeSelected)?.extension ?? ""}`;

			  const created = await handleOnSubmit(data);

			  if (!template._id && created) {
			    if (closeModal) {
			      closeModal();
			    }
			  }
      })}
    >
      <Grid container spacing={2}>
        {!isEdit && (
        <Grid item xs={12} sm={6}>
          <Controller
            name="kindTem"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <SelectField
                {...field}
                handleChange={onChange}
                value={field.value || ''}
                data={getCodeSelector(t)}
                dataKey="key"
                dataValue="value"
                dataText="text"
                label={t('documents:kind-tem')}
                disabled={controlsDisabled}
              />
            )}
          />
        </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <Controller
            name="code"
            control={control}
            render={({ field: { ref, onChange, ...field } }) => (
              <TextField
                {...field}
                value={field.value}
                inputRef={ref}
                label={t('documents:code')}
                disabled={isEdit || controlsDisabled}
                onChange={(e) => {
								  e.target.value = e.target.value.replace(
								    /[^a-z0-9-]/g,
								    ''
								  );
								  onChange(e);
                }}
                error={!!errors.code}
                helperText={errors.code?.message || t('validations:required')}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl variant="filled" fullWidth disabled={controlsDisabled}>
            <InputLabel>{t('documents:deliver')}</InputLabel>
            <Controller
              name="data.deliver"
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <Select
                  {...field}
                  className={classes.input}
                  variant="filled"
                  multiple
                  onChange={(e) => {
									  onChange(e.target.value);
                  }}
                  disabled={controlsDisabled}
                  value={field.value || []}
                  input={<Input />}
                  renderValue={(selected) => (selected as any).join(', ')}
                >
                  {partyRoles.map((roll) => (
                    <MenuItem key={roll.code} value={roll.code}>
                      <Checkbox
                        checked={
													Array.isArray(field.value)
													&& field.value.some((data) => data === roll.code)
												}
                      />
                      <ListItemText primary={roll.description} />
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl variant="filled" fullWidth disabled={controlsDisabled}>
            <InputLabel>{t('documents:party')}</InputLabel>
            <Controller
              name="data.party"
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <Select
                  {...field}
                  className={classes.input}
                  variant="filled"
                  multiple
                  onChange={(e) => {
									  onChange(e.target.value);
                  }}
                  disabled={controlsDisabled}
                  value={field.value || []}
                  input={<Input />}
                  renderValue={(selected) => (selected as any).join(', ')}
                >
                  {partyRoles.map((roll) => (
                    <MenuItem key={roll.code} value={roll.code}>
                      <Checkbox
                        checked={
													Array.isArray(field.value)
													&& field.value.some((data) => data === roll.code)
												}
                      />
                      <ListItemText primary={roll.description} />
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl variant="filled" fullWidth disabled={controlsDisabled}>
            <InputLabel>{t('documents:signer')}</InputLabel>
            <Controller
              name="data.signer"
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <Select
                  {...field}
                  className={classes.input}
                  variant="filled"
                  multiple
                  onChange={(e) => {
									  onChange(e.target.value);
                  }}
                  disabled={controlsDisabled}
                  value={field.value || []}
                  input={<Input />}
                  renderValue={(selected) => (selected as any).join(', ')}
                >
                  {partyRoles.map((roll) => (
                    <MenuItem key={roll.code} value={roll.code}>
                      <Checkbox
                        checked={
													Array.isArray(field.value)
													&& field.value.some((data) => data === roll.code)
												}
                      />
                      <ListItemText primary={roll.description} />
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                value={field.value}
                inputRef={ref}
                label={t('documents:name')}
                disabled={controlsDisabled}
                error={!!errors.name}
                helperText={errors.name?.message || t('validations:required')}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl variant="filled" fullWidth disabled={controlsDisabled}>
            <InputLabel>{t('documents:per')}</InputLabel>
            <Controller
              name="per"
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <Select
                  {...field}
                  className={classes.input}
                  variant="filled"
                  multiple
                  onChange={(e) => {
									  onChange(e.target.value);
                  }}
                  disabled={controlsDisabled}
                  value={field.value || []}
                  input={<Input />}
                  renderValue={(selected) => (selected as any).join(', ')}
                >
                  {perElemts.map((per) => (
                    <MenuItem
                      key={per.code}
                      value={per.code}
                      disabled={
												Array.isArray(field.value)
												&& field.value.length >= 2
												&& !field.value.some((data) => data === per.code)
											}
                    >
                      <Checkbox
                        checked={
													Array.isArray(field.value)
													&& field.value.some((data) => data === per.code)
												}
                      />
                      <ListItemText primary={per.description} />
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={isEdit ? 6 : 4}>
          <Controller
            name="triggers.transaction_type"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <SelectField
                {...field}
                handleChange={onChange}
                value={field.value}
                data={transactionTypes}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('documents:transaction-type')}
                disabled={controlsDisabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={isEdit ? 6 : 4}>
          <Controller
            name="triggers.type"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <SelectField
                {...field}
                handleChange={onChange}
                value={field.value}
                data={types}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('documents:type')}
                disabled={controlsDisabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={isEdit ? 6 : 4}>
          <Controller
            name="triggers.workflow"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <SelectField
                {...field}
                handleChange={onChange}
                value={field.value}
                data={workflows}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('documents:workflow')}
                disabled={controlsDisabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={isEdit ? 6 : 4}>
          <Controller
            name="triggers.projected_payments"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <SelectField
                {...field}
                handleChange={onChange}
                value={field.value}
                data={projectedPaymentsListOptions}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('disclosure:projected-payments')}
                disabled={controlsDisabled}
              />
            )}
          />
        </Grid>
        {!hideSubmit && (
        <Grid item xs={12} className={classes.saveAction}>
          <SaveButton
            disabled={!isValid || controlsDisabled}
            loading={isSubmitting}
            type="submit"
            customText={t('common:save')}
          />
        </Grid>
        )}
      </Grid>
    </form>
  );
};
